import { ENEMY_GAIT, turnRateOf, turnToward } from "./enemyGait";
import type { EnemyView } from "./enemyView";

/**
 * How a planted walker (a measured stride, a foot IK) turns (enemyFeel.ts stepUnit runs it each frame, before its feet
 * are placed). At a road corner the sim's heading swings in a tick or two; a body swung round its root at 7 rad/s while
 * it carried on along the new leg crabbed sideways and swept its planted feet across the road (0.5-4 times its walking
 * speed in the slip6 probe). Instead:
 *
 * - its drawn path is rounded (roundPath): the root follows the sim's line with its heading turning at the walkers'
 *   turn rate, so the body faces the way it moves through the corner; it strays from the sim's corner by about
 *   speed / turn rate;
 * - it locks first (turnBody): on the frame its heading starts to swing it does not turn yet, so the foot lock
 *   (footIk.ts) pins the planted soles where they stand before anything moves, and holds them through the turn.
 *
 * A body pivoted about its pinned feet was tried too: easing its offset home slid the bosses' planted soles.
 * Anything else (a glide, a flyer, a raider without an IK) turns about its root as before.
 */

/** Turns `view` toward `want` (rad; NaN: it is not moving) over `dt`; true while it turns (or is about to). */
export function turnBody(view: EnemyView, want: number, dt: number): boolean {
  const b = view.body, yaw0 = b.rotation.y;
  const off = Number.isNaN(want) ? 0 : Math.abs(Math.atan2(Math.sin(want - yaw0), Math.cos(want - yaw0)));
  if (off <= ENEMY_GAIT.lock.turnEps) { view.turnHeld = false; return false; }
  if (view.age < ENEMY_GAIT.turn.spawnFace) { b.rotation.y = want; return false; }
  if (view.ik !== null && view.stride > 0 && !view.turnHeld) { view.turnHeld = true; return true; }   // lock now, turn next
  b.rotation.y = turnToward(yaw0, want, dt, view.path.rate);
  return true;
}

/**
 * Rounds a planted walker's drawn path: `view.root.position` comes in at the sim's (interpolated) position, moving along
 * `simHead` (rad; NaN: still), and leaves on the rounded path; returns the heading it moves along (rad, before HQ_YAW),
 * NaN when it does not move. The heading follows the sim's, turned at its turn rate (turnRateOf), plus a steer back onto the
 * sim's line (`steer` rad per cell of sideways error, at most `steerMax`); the path covers the ground the sim covered
 * this frame plus a catch-up on what it lags along the line, and snaps to the sim after a jump.
 */
export function roundPath(view: EnemyView, simHead: number, dt: number): number {
  const p = view.root.position, t = view.path, T = ENEMY_GAIT.turn;
  const moved = Math.hypot(p.x - t.sx, p.z - t.sz);
  t.sx = p.x; t.sz = p.z;
  const ex = p.x - t.x, ez = p.z - t.z;
  if (!t.set || Math.hypot(ex, ez) > T.snap || moved > ENEMY_GAIT.maxStep) { t.set = true; t.x = p.x; t.z = p.z; t.head = NaN; return NaN; }
  if (Number.isNaN(simHead)) { p.x = t.x; p.z = t.z; return NaN; }
  const fx = Math.sin(simHead), fz = Math.cos(simHead);
  const along = ex * fx + ez * fz, side = ex * fz - ez * fx;              // the error along the sim's line and across it
  const want = simHead + Math.max(-T.steerMax, Math.min(T.steerMax, T.steer * side));
  t.rate = turnRateOf(moved / Math.max(1e-6, dt), view.foot.half);
  t.head = Number.isNaN(t.head) ? simHead : turnToward(t.head, want, dt, t.rate);
  const k = Math.exp(-T.catchUp * dt), step = Math.max(0, along - (along - moved) * k);   // the lag left shrinks by k a frame
  t.x += Math.sin(t.head) * step; t.z += Math.cos(t.head) * step;
  p.x = t.x; p.z = t.z;
  return t.head;
}
